// src/context/AuthContext.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { apiUrl } from "../lib/apiBase.js";
import {
  defaultReportBranding,
  fetchReportBranding,
  saveReportBranding,
} from "../utils/reportBranding";
import { saveProgress } from "../utils/progressStore";
import { saveSupplements } from "../utils/supplementsStore";
import { claimSession } from "../lib/claimSession";

const AuthContext = createContext(null);

// Adjust this path to match your backend (e.g., /api/login, /api/auth/login, /login, etc.)
const LOGIN_ENDPOINT = apiUrl("/auth/login");
const LOGOUT_ENDPOINT = apiUrl("/auth/logout");
const ME_ENDPOINT = apiUrl("/auth/me");

// Local storage keys
const USER_STORAGE_KEY = "claimsupp_user";
const TOKEN_STORAGE_KEY = "claimsupp_token";

const normalizeEmail = (value) => String(value || "").trim().toLowerCase();
const normalizePassword = (value) => String(value ?? "");
const REQUIRES_PASSWORD = true;
const ERROR_COPY = {
  email_required: "Email is required",
  login_disabled: "Login is disabled until an account is created.",
  signup_disabled: "Signups are currently disabled.",
  user_exists: "An account with this email already exists.",
  password_too_short: "Password is too short.",
  access_denied: "Access denied. Request access to create an account.",
  password_required: "Password is required",
  invalid_password: "Invalid password",
  auth_unavailable: "Auth services are unavailable. Try again shortly.",
  auth_required: "Please sign in again.",
  auth_invalid: "Please sign in again.",
};

function pickAuthErrorMessage(payload, fallback) {
  const code = payload?.error || payload?.code;
  if (code && ERROR_COPY[code]) return ERROR_COPY[code];
  return payload?.message || fallback;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem(USER_STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    let cancelled = false;
    const clearToken = () => {
      try {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
      } catch {
        // ignore storage failures
      }
    };
    async function bootstrap() {
      try {
        const res = await fetch(ME_ENDPOINT, { credentials: "include" });
        const payload = await res.json().catch(() => null);
        if (!res.ok) {
          if (!cancelled) {
            setUser(null);
            clearToken();
          }
          return;
        }
        const nextUser = payload?.user || null;
        if (!cancelled) setUser(nextUser);
        try {
          const branding = await fetchReportBranding();
          if (!cancelled && branding) saveReportBranding(branding);
        } catch {
          // ignore branding sync failures
        }
      } catch {
        if (!cancelled) {
          setUser(null);
          clearToken();
        }
      }
    }
    bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (user) {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(USER_STORAGE_KEY);
    }
  }, [user]);

  async function login(email, password) {
    const normalizedEmail = normalizeEmail(email);
    const normalizedPassword = normalizePassword(password);

    if (!normalizedEmail) {
      throw new Error("Email is required");
    }

    if (!normalizedPassword) {
      throw new Error("Password is required");
    }

    const res = await fetch(LOGIN_ENDPOINT, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: normalizedEmail,
        password: normalizedPassword || undefined,
      }),
    });
    const payload = await res.json().catch(() => null);
    if (!res.ok) {
      throw new Error(pickAuthErrorMessage(payload, "Unable to sign in. Please try again."));
    }
    const nextUser = payload?.user || { email: normalizedEmail };
    setUser(nextUser);
    try {
      if (payload?.token) {
        localStorage.setItem(TOKEN_STORAGE_KEY, payload.token);
      } else {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
      }
    } catch {
      // ignore storage failures
    }
    try {
      const branding = await fetchReportBranding();
      if (branding) saveReportBranding(branding);
    } catch {
      // ignore branding sync failures
    }
    return nextUser;
  }

  async function signup({ email, password, fullName, companyName }) {
    const normalizedEmail = normalizeEmail(email);
    const normalizedPassword = normalizePassword(password);

    if (!normalizedEmail) {
      throw new Error("Email is required");
    }
    if (!normalizedPassword) {
      throw new Error("Password is required");
    }

    const res = await fetch(apiUrl("/auth/signup"), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: normalizedEmail,
        password: normalizedPassword,
        fullName: fullName || undefined,
        companyName: companyName || undefined,
      }),
    });
    const payload = await res.json().catch(() => null);
    if (!res.ok) {
      throw new Error(pickAuthErrorMessage(payload, "Unable to sign up. Please try again."));
    }
    const nextUser = payload?.user || { email: normalizedEmail };
    setUser(nextUser);
    try {
      if (payload?.token) {
        localStorage.setItem(TOKEN_STORAGE_KEY, payload.token);
      } else {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
      }
    } catch {
      // ignore storage failures
    }
    try {
      const branding = await fetchReportBranding();
      if (branding) saveReportBranding(branding);
    } catch {
      // ignore branding sync failures
    }
    return nextUser;
  }

  async function logout() {
    try {
      await fetch(LOGOUT_ENDPOINT, { method: "POST", credentials: "include" });
    } catch {
      // ignore
    } finally {
      setUser(null);
      try {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
      } catch {
        // ignore
      }
      try {
        localStorage.removeItem(USER_STORAGE_KEY);
      } catch {
        // ignore
      }
      try {
        saveProgress([]);
        saveSupplements([]);
      } catch {
        // ignore
      }
      try {
        claimSession.clearAll();
      } catch {
        // ignore
      }
      try {
        saveReportBranding(defaultReportBranding());
      } catch {
        // ignore
      }
    }
  }

  const value = {
    user,
    isAuthenticated: !!user,
    login,
    signup,
    logout,
    requiresPassword: REQUIRES_PASSWORD,
    get token() {
      try {
        return localStorage.getItem(TOKEN_STORAGE_KEY);
      } catch {
        return null;
      }
    },
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
